import React, { useState, useEffect } from "react";
import { Icon } from '@iconify/react/dist/iconify.js';
import toast from "react-hot-toast";
import { getAdvertisementApi, createAdvertisementApi, updateAdvertisementApi, deleteAdvertisementApi, ImageUrl } from "../../helper/apiHelper";

const AdvertiseComp = () => {
    const [advertisements, setAdvertisements] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [newAd, setNewAd] = useState({ id: '', image: '', redirectUrl: '' });

    // Fetch advertisements on component mount
    useEffect(() => {
        fetchAdvertisements();
    }, []);

    const fetchAdvertisements = async () => {
        setIsLoading(true);
        try {
            const response = await getAdvertisementApi();
            if (response.data) {
                // Map API response to component state
                const mappedAds = response.data.map(ad => ({
                    id: ad.id,
                    image: `${ImageUrl}/${ad.image}`, // Construct full image URL
                    redirectUrl: ad.url
                }));
                setAdvertisements(mappedAds);
            }
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to fetch advertisements",
                { id: "fetch-ads-error" }
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!newAd.redirectUrl) {
            toast.error("Please enter a redirection URL", { id: "url-required" });
            return;
        }

        if (!isEditing && !selectedFile) {
            toast.error("Please select an image", { id: "image-required" });
            return;
        }

        setSaveLoading(true);
        try {
            const formData = new FormData();
            formData.append('url', newAd.redirectUrl);

            if (selectedFile) {
                formData.append('image', selectedFile);
            }

            if (isEditing) {
                await updateAdvertisementApi(formData, newAd.id);
                toast.success("Advertisement updated successfully", { id: "ad-success" });
            } else {
                await createAdvertisementApi(formData);
                toast.success("Advertisement added successfully", { id: "ad-success" });
            }

            setShowModal(false);
            resetForm();
            fetchAdvertisements(); // Refresh the list
        } catch (error) {
            toast.error(error || "Operation failed", { id: "ad-error" });
        } finally {
            setSaveLoading(false);
        }
    };

    const resetForm = () => {
        setNewAd({ id: '', image: '', redirectUrl: '' });
        setSelectedFile(null);
        setIsEditing(false);
    };

    const handleEdit = (item) => {
        setIsEditing(true);
        setNewAd(item);
        setShowModal(true);
    };

    const handleDeleteClick = (id) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!deleteId) return;

        setIsLoading(true);
        try {
            await deleteAdvertisementApi(deleteId);
            toast.success("Advertisement deleted successfully", { id: "delete-success" });
            setShowDeleteModal(false);
            setDeleteId(null);
            fetchAdvertisements(); // Refresh the list
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to delete advertisement",
                { id: "delete-error" }
            );
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        return () => {
            if (newAd.image) {
                URL.revokeObjectURL(newAd.image);
            }
        };
    }, [newAd.image]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        const img = new Image();

        reader.onload = (event) => {
            img.src = event.target.result;
        };

        img.onload = () => {
            const TARGET_WIDTH = 1372;
            const TARGET_HEIGHT = 456;

            const canvas = document.createElement("canvas");
            canvas.width = TARGET_WIDTH;
            canvas.height = TARGET_HEIGHT;

            const ctx = canvas.getContext("2d");

            // Maintain aspect ratio (center crop)
            const scale = Math.max(
                TARGET_WIDTH / img.width,
                TARGET_HEIGHT / img.height
            );

            const x = (TARGET_WIDTH - img.width * scale) / 2;
            const y = (TARGET_HEIGHT - img.height * scale) / 2;

            ctx.drawImage(
                img,
                x,
                y,
                img.width * scale,
                img.height * scale
            );

            // 🔑 Convert canvas to REAL file
            canvas.toBlob(
                (blob) => {
                    const resizedFile = new File(
                        [blob],
                        file.name,
                        { type: "image/jpeg", lastModified: Date.now() }
                    );

                    // ✅ This is now the resized image
                    setSelectedFile(resizedFile);

                    // Preview
                    const previewUrl = URL.createObjectURL(resizedFile);
                    setNewAd((prev) => ({
                        ...prev,
                        image: previewUrl
                    }));
                },
                "image/jpeg",
                0.9
            );
        };

        reader.readAsDataURL(file);
    };



    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden gallery-scale">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between gap-2" style={{ flexWrap: "wrap" }}>
                <h6 className="text-lg fw-semibold mb-0">Offers & Advertisements</h6>
                <button
                    type="button"
                    className="btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2"
                    onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}
                >
                    <Icon icon="ic:baseline-plus" className="text-xl" />
                    Add Offer Image
                </button>
            </div>


            <div className="card-body p-24">
                {isLoading ? (
                    <div className="text-center py-40">
                        <div className="spinner-border text-primary" role="status" />
                    </div>
                ) : advertisements.length === 0 ? (
                    <div className="text-center py-40 text-muted fw-medium">
                        No advertisements found.
                    </div>
                ) : (
                    <div className="row gy-4">
                        {advertisements.map((item) => (
                            <div className="col-xxl-6 col-md-4 col-sm-6" key={item.id}>
                                <div className="hover-scale-img border radius-16 overflow-hidden p-8 position-relative group">
                                    <div className="radius-12 overflow-hidden position-relative">
                                        <img
                                            src={item.image}
                                            alt=""
                                            className="hover-scale-img__img w-100 h-100 object-fit-cover"
                                            style={{ maxHeight: '230px' }}
                                        />
                                        <div className="position-absolute top-50 start-50 translate-middle d-flex gap-2">
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle bg-white text-primary-600 shadow-sm"
                                            >
                                                <Icon icon="lucide:edit" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteClick(item.id)}
                                                className="w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle bg-white text-danger-600 shadow-sm"
                                            >
                                                <Icon icon="fluent:delete-24-regular" />
                                            </button>
                                        </div>
                                    </div>
                                    {item.redirectUrl && (
                                        <div className="mt-2 text-center">
                                            <small className="text-muted text-truncate d-block" style={{ maxWidth: '100%' }}>
                                                {item.redirectUrl}
                                            </small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <>
                    <div className="modal fade show" style={{ display: 'block' }}>
                        <div className="modal-dialog modal-md modal-dialog-centered" style={{ zIndex: 11122 }}>
                            <div className="modal-content radius-16 bg-base">
                                <div className="modal-header py-16 px-24 border-bottom">
                                    <h1 className="modal-title fs-5">{isEditing ? "Edit Offer Image" : "Add Offer Image"}</h1>
                                    <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
                                </div>
                                <div className="modal-body p-24">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Redirection URL</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter redirection URL"
                                            value={newAd.redirectUrl}
                                            onChange={(e) => setNewAd({ ...newAd, redirectUrl: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Upload Image <span className="text-muted fs-12" style={{ fontWeight: "normal" }} >(Recommended Size: 1372px x 456px)</span></label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                        />
                                    </div>
                                    {/* {newAd.image && (
                                        <div className="mb-3">
                                            <img
                                                src={newAd.image}
                                                alt="Preview"
                                                className="w-100 h-100 object-fit-cover radius-8"
                                                style={{ maxHeight: '200px' }}
                                            />
                                        </div>
                                    )} */}



                                    {newAd.image && (

                                        <>

                                            <div style={{ marginBottom: "2px", fontSize: "12px", fontWeight: "500" }}>
                                                <span>Image Preview</span>
                                            </div>
                                            <img
                                                src={newAd.image}
                                                alt="Preview"
                                                style={{
                                                    width: "100%",
                                                    maxHeight: "200px",
                                                    aspectRatio: "1372 / 456",
                                                    objectFit: "cover",
                                                    borderRadius: "8px",
                                                    border: "1px solid #e5e7eb"
                                                }}
                                            />
                                        </>
                                    )}

                                    <div className="d-flex align-items-center justify-content-end gap-3 mt-24">
                                        <button
                                            type="button"
                                            className="btn btn-secondary border border-secondary-600 text-md px-24 py-10 radius-8"
                                            onClick={() => setShowModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary border border-primary-600 text-md px-32 py-10 radius-8"
                                            onClick={handleSave}
                                            disabled={saveLoading}
                                        >
                                            {isEditing ? 'Update' : 'Save'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
                    </div>
                </>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <>
                    <div className="modal fade show" style={{ display: 'block' }}>
                        <div className="modal-dialog modal-dialog-centered" style={{ zIndex: 11122 }}>
                            <div className="modal-content radius-16 bg-base">
                                <div className="modal-header py-16 px-24 border-bottom">
                                    <h1 className="modal-title fs-5">Confirm Delete</h1>
                                    <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
                                </div>
                                <div className="modal-body p-24">
                                    <p className="text-center mb-0">Are you sure you want to delete this advertisement?</p>
                                </div>
                                <div className="modal-footer py-16 px-24 border-top">
                                    <button
                                        type="button"
                                        className="btn btn-secondary border border-secondary-600 text-md px-24 py-10 radius-8"
                                        onClick={() => setShowDeleteModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger border border-danger-600 text-md px-32 py-10 radius-8"
                                        onClick={confirmDelete}
                                        disabled={isLoading}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
                    </div>
                </>
            )}
        </div>
    );
};

export default AdvertiseComp;
